import React, { useState, useEffect } from 'react';
import { Shield, ShieldCheck, RefreshCw, Clock, AlertTriangle, ArrowUp, AlertCircle, Plus, Calendar, ChevronDown } from 'lucide-react';
import securityApi from '../api/securityApi';
import LogoutButton from '../components/LogoutButton';
// We'll use a date picker library - you'll need to install this
// npm install react-datepicker
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
// We'll use a charting library - you'll need to install this
// npm install recharts
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar } from 'recharts';

const DashboardPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dashboardStats, setDashboardStats] = useState({
    securityScore: { current: 0, improvement: 0 },
    vulnerabilities: { critical: 0, high: 0, medium: 0, low: 0 },
    meanTimeToRemediate: { days: 0, improvement: '0%' }
  });
  const [latestFindings, setLatestFindings] = useState([]);
  const [lastScanTime, setLastScanTime] = useState(null);
  const [prMetrics, setPrMetrics] = useState({
    timeSeriesData: [],
    summary: {
      totalPrsCreated: 0,
      totalPrsMerged: 0,
      totalSecurityFixes: 0,
      mergeRate: '0%'
    }
  });
  const [mergedPullRequests, setMergedPullRequests] = useState([]);
  const [healthMetrics, setHealthMetrics] = useState(null);

  // Date range state
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [interval, setInterval] = useState('day');

  // Date range presets
  const dateRangePresets = [
    { label: 'Last 7 days', days: 7 },
    { label: 'Last 30 days', days: 30 },
    { label: 'Last 90 days', days: 90 },
    { label: 'Last 12 months', days: 365 },
    { label: 'All time', days: null }
  ];

  // Fetch dashboard data (interval-sensitive)
  useEffect(() => {
    fetchDashboardData();
    fetchMergedPullRequests();
  }, [startDate, endDate, interval]);

  // Health metrics only depend on the date range, not the grouping interval.
  // Uses a stale flag so overlapping requests don't overwrite with old data.
  useEffect(() => {
    let stale = false;
    const loadHealthMetrics = async () => {
      try {
        const formattedStartDate = startDate ? formatDateForApi(startDate) : null;
        const formattedEndDate = endDate ? formatDateForApi(endDate) : null;
        const health = await securityApi.getDashboardHealthMetrics(formattedStartDate, formattedEndDate);
        if (!stale) setHealthMetrics(health);
      } catch (err) {
        console.error('Error fetching health metrics:', err);
        if (!stale) setHealthMetrics(null);
      }
    };
    loadHealthMetrics();
    return () => { stale = true; };
  }, [startDate, endDate]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Format dates for API
      const formattedStartDate = startDate ? formatDateForApi(startDate) : null;
      const formattedEndDate = endDate ? formatDateForApi(endDate) : null;

      // Fetch dashboard stats with date range
      const stats = await securityApi.getDashboardStats(formattedStartDate, formattedEndDate);
      setDashboardStats(stats);

      // Fetch latest findings with date range
      const findings = await securityApi.getLatestFindings(5, formattedStartDate, formattedEndDate);
      setLatestFindings(findings.findings || []);

      // Fetch PR metrics with date range
      const metrics = await securityApi.getPRMetrics(formattedStartDate, formattedEndDate, interval);
      setPrMetrics(metrics);

      // Set last scan time
      if (stats.lastScanTime) {
        setLastScanTime(stats.lastScanTime);
      }

      setLoading(false);
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError('Failed to load dashboard data. Please try again.');
      setLoading(false);
    }
  };

  // North-star health metrics (merge rate, time-to-merge). Fetched independently of
  // the main dashboard load so a slow/hanging health-metrics request never blocks the
  // global loading state — the metric cards degrade to '—' until this resolves.
  // (Implementation is inlined in the useEffect above with a stale-request guard.)

  const fetchMergedPullRequests = async () => {
    try {
      // Format dates for API
      const formattedStartDate = startDate ? formatDateForApi(startDate) : null;
      const formattedEndDate = endDate ? formatDateForApi(endDate) : null;
      const data = await securityApi.getMergedPullRequests(5, formattedStartDate, formattedEndDate);
      setMergedPullRequests(data.merged_pull_requests || []);
    } catch (err) {
      console.error('Error fetching merged pull requests:', err);
      setMergedPullRequests([]);
    }
  };

  // Helper function to format date for API (local date, not UTC, to avoid
  // off-by-one in timezones ahead of UTC where local midnight is still the
  // previous day in UTC).
  const formatDateForApi = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  // Apply date range preset
  const applyDateRangePreset = (days) => {
    if (days === null) {
      // All time
      setStartDate(null);
      setEndDate(null);
    } else {
      const end = new Date();
      const start = new Date();
      start.setDate(end.getDate() - days);
      setStartDate(start);
      setEndDate(end);
    }
    setDatePickerOpen(false);
  };

  // No longer needed

  // Format relative time
  const formatRelativeTime = (timestamp) => {
    if (!timestamp) return 'N/A';

    const date = new Date(timestamp);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);

    if (diffInSeconds < 60) return 'just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
    return `${Math.floor(diffInSeconds / 86400)} days ago`;
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-8 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <RefreshCw className="w-10 h-10 text-teal-600 animate-spin" />
          <p className="text-gray-700 text-lg">Loading dashboard data...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 p-8 flex items-center justify-center">
        <div className="bg-white border border-gray-200 shadow-sm rounded-lg p-8 max-w-md">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 text-center mb-4">Error Loading Dashboard</h2>
          <p className="text-gray-600 text-center mb-6">{error}</p>
          <button
            onClick={fetchDashboardData}
            className="w-full bg-teal-600 text-white py-2 rounded-lg hover:bg-teal-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 mb-1">Dashboard Overview</h1>
          </div>
          <div className="flex items-center gap-4">
            {/* Date Range Selector */}
            <div className="relative">
              <button
                onClick={() => setDatePickerOpen(!datePickerOpen)}
                className="bg-white border border-gray-200 hover:border-gray-300 text-gray-700 px-4 py-2 rounded-lg flex items-center gap-2 transition-colors shadow-sm"
              >
                <Calendar className="w-4 h-4" />
                <span>
                  {startDate && endDate
                    ? `${formatDateForApi(startDate)} - ${formatDateForApi(endDate)}`
                    : 'All Time'}
                </span>
                <ChevronDown className="w-4 h-4" />
              </button>

              {/* Date Range Dropdown */}
              {datePickerOpen && (
                <div className="absolute right-0 mt-2 w-72 bg-white border border-gray-200 rounded-lg shadow-lg z-10 p-4">
                  <h4 className="text-gray-900 font-semibold mb-2">Select Date Range</h4>

                  {/* Presets */}
                  <div className="mb-4">
                    <p className="text-sm text-gray-500 mb-2">Presets</p>
                    <div className="space-y-2">
                      {dateRangePresets.map((preset, index) => (
                        <button
                          key={index}
                          onClick={() => applyDateRangePreset(preset.days)}
                          className="bg-teal-600 text-white block w-full text-left px-3 py-2 text-sm rounded hover:bg-teal-700"
                        >
                          {preset.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Custom Range */}
                  <div>
                    <p className="text-sm text-gray-500 mb-2">Custom Range</p>
                    <div className="flex gap-2 mb-3">
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Start Date</label>
                        <DatePicker
                          selected={startDate}
                          onChange={date => setStartDate(date)}
                          selectsStart
                          startDate={startDate}
                          endDate={endDate}
                          className="w-full p-2 border border-gray-200 rounded text-sm"
                          dateFormat="yyyy-MM-dd"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">End Date</label>
                        <DatePicker
                          selected={endDate}
                          onChange={date => setEndDate(date)}
                          selectsEnd
                          startDate={startDate}
                          endDate={endDate}
                          minDate={startDate}
                          className="w-full p-2 border border-gray-200 rounded text-sm"
                          dateFormat="yyyy-MM-dd"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Interval Selection */}
                  <div className="mb-4">
                    <p className="text-sm text-gray-500 mb-2">Group By</p>
                    <div className="flex gap-2">
                      {['day', 'week', 'month'].map((option) => (
                        <button
                          key={option}
                          onClick={() => setInterval(option)}
                          className={`px-3 py-1 text-sm rounded ${
                            interval === option
                              ? 'bg-teal-600 text-white'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          {option.charAt(0).toUpperCase() + option.slice(1)}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Apply Button */}
                  <button
                    onClick={() => setDatePickerOpen(false)}
                    className="w-full bg-teal-600 text-white py-2 rounded-lg hover:bg-teal-700 transition-colors"
                  >
                    Apply
                  </button>
                </div>
              )}
            </div>

            <button
              onClick={fetchDashboardData}
              className="bg-teal-600 hover:bg-teal-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors shadow-sm"
            >
              <RefreshCw className="w-4 h-4" />
              <span>Refresh</span>
            </button>
          </div>
        </div>

        <h2 className="text-3xl font-bold text-gray-900 mb-6">Security Dashboard</h2>

        {/* Dev-vs-lead framing: developers never need this app; the fix lives in
            their GitHub PRs. This dashboard is for security & compliance leads. */}
        <div className="bg-teal-50 border border-teal-200 rounded-lg px-4 py-3 mb-6 flex items-start gap-3">
          <ShieldCheck className="w-5 h-5 text-teal-600 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-teal-900">
            <span className="font-semibold">For developers, everything happens in your GitHub pull requests</span> —
            fixes arrive as reviewable PRs, so there's nothing to check here. This dashboard is for
            security &amp; compliance leads tracking how fast issues get fixed.
          </p>
        </div>

        {/* North-star health metrics — how fast issues get fixed and how often our
            PRs are accepted, not how many findings we generated. */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          {/* Merge rate (with health threshold) */}
          <div className="bg-white border border-gray-100 shadow-sm rounded-lg p-5">
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm text-gray-600">PR Merge Rate</span>
              {healthMetrics && healthMetrics.mergeRateHealthy != null && (
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                  healthMetrics.mergeRateHealthy
                    ? 'bg-green-100 text-green-700'
                    : 'bg-red-100 text-red-700'
                }`}>
                  {healthMetrics.mergeRateHealthy
                    ? 'Healthy'
                    : (healthMetrics.mergeRateThresholdPct != null
                        ? `Below ${healthMetrics.mergeRateThresholdPct}%`
                        : 'Unhealthy')}
                </span>
              )}
            </div>
            <div className="text-3xl font-bold text-gray-900">
              {healthMetrics && healthMetrics.mergeRatePct != null ? `${healthMetrics.mergeRatePct}%` : '—'}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              {healthMetrics && healthMetrics.totalPrsMerged != null && healthMetrics.totalPrsCreated != null
                ? `${healthMetrics.totalPrsMerged}/${healthMetrics.totalPrsCreated} PRs merged`
                : '—'}
            </div>
          </div>

          {/* Median time to fix (opened -> merged) */}
          <div className="bg-white border border-gray-100 shadow-sm rounded-lg p-5">
            <span className="text-sm text-gray-600">Median Time to Fix</span>
            <div className="text-3xl font-bold text-gray-900 mt-1">
              {healthMetrics && healthMetrics.medianHoursOpenToMerge != null
                ? `${healthMetrics.medianHoursOpenToMerge}h`
                : '—'}
            </div>
            <div className="text-xs text-gray-500 mt-1">From PR opened to merged</div>
          </div>

          {/* Open PRs (attention budget) */}
          <div className="bg-white border border-gray-100 shadow-sm rounded-lg p-5">
            <span className="text-sm text-gray-600">Open Fix PRs</span>
            <div className="text-3xl font-bold text-gray-900 mt-1">
              {healthMetrics && healthMetrics.openPrCount != null ? healthMetrics.openPrCount : '—'}
            </div>
            <div className="text-xs text-gray-500 mt-1">Awaiting review — capped per repo</div>
          </div>

          {/* Avg Review Time (devMinutesPerFix) */}
          <div className="bg-white border border-gray-100 shadow-sm rounded-lg p-5">
            <span className="text-sm text-gray-600">Median Review Time</span>
            <div className="text-3xl font-bold text-gray-900 mt-1">
              {healthMetrics && healthMetrics.devMinutesPerFix != null
                ? `${healthMetrics.devMinutesPerFix}m`
                : '—'}
            </div>
            <div className="text-xs text-gray-500 mt-1">Median minutes to first review</div>
          </div>
        </div>

        <div className="flex justify-between items-center mb-2">
          <span className="text-gray-400 text-xs uppercase tracking-wide">Finding counts (context, not a score)</span>
          <span className="text-gray-500 text-sm">Last scan: {formatRelativeTime(lastScanTime)}</span>
        </div>

        {/* Security Score and Vulnerabilities in a single row */}
        <div className="grid grid-cols-6 gap-4 mb-6">
          {/* Security Score */}
          <div className="bg-white border border-gray-100 shadow-sm rounded-lg p-4 flex flex-col items-center justify-center">
            <div className="relative w-12 h-12 mb-2">
              <div className="absolute inset-0 rounded-full bg-teal-100 flex items-center justify-center">
                <span className="text-teal-700 font-bold">F</span>
              </div>
            </div>
            <div className="text-sm text-gray-600 mb-1">Security Score</div>
            <div className="text-xl font-bold text-gray-900">{dashboardStats.securityScore?.current || 0}/100</div>
          </div>

          {/* Critical */}
          <div className="bg-white border border-gray-100 shadow-sm rounded-lg p-4 flex flex-col items-center justify-center">
            <div className="text-3xl font-bold text-red-600">{dashboardStats.vulnerabilities?.critical || 0}</div>
            <div className="text-sm text-red-600">Critical</div>
          </div>

          {/* High */}
          <div className="bg-white border border-gray-100 shadow-sm rounded-lg p-4 flex flex-col items-center justify-center">
            <div className="text-3xl font-bold text-orange-600">{dashboardStats.vulnerabilities?.high || 0}</div>
            <div className="text-sm text-orange-600">High</div>
          </div>

          {/* Medium */}
          <div className="bg-white border border-gray-100 shadow-sm rounded-lg p-4 flex flex-col items-center justify-center">
            <div className="text-3xl font-bold text-yellow-600">{dashboardStats.vulnerabilities?.medium || 0}</div>
            <div className="text-sm text-yellow-600">Medium</div>
          </div>

          {/* Low */}
          <div className="bg-white border border-gray-100 shadow-sm rounded-lg p-4 flex flex-col items-center justify-center">
            <div className="text-3xl font-bold text-blue-600">{dashboardStats.vulnerabilities?.low || 0}</div>
            <div className="text-sm text-blue-600">Low</div>
          </div>

          {/* Fixed */}
          <div className="bg-white border border-gray-100 shadow-sm rounded-lg p-4 flex flex-col items-center justify-center">
            <div className="text-3xl font-bold text-green-600">{dashboardStats.vulnerabilities?.fixed || 0}</div>
            <div className="text-sm text-green-600">Fixed</div>
          </div>
        </div>

        {/* PR Metrics Chart */}
        <div className="bg-white border border-gray-100 shadow-sm rounded-lg p-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Pull Request Metrics</h3>
            <div className="text-sm text-gray-500">
              {prMetrics.dateRange?.startDate && prMetrics.dateRange?.endDate ? (
                <span>
                  {new Date(prMetrics.dateRange.startDate).toLocaleDateString()} - {new Date(prMetrics.dateRange.endDate).toLocaleDateString()}
                </span>
              ) : (
                <span>All Time</span>
              )}
            </div>
          </div>

          {/* PR Metrics Summary */}
          <div className="grid grid-cols-4 gap-4 mb-6">
            <div className="bg-gray-50 border border-gray-100 rounded p-4 text-center">
              <div className="text-2xl font-bold text-gray-900">{prMetrics.summary?.totalPrsCreated || 0}</div>
              <div className="text-sm text-gray-500">PRs Created</div>
            </div>
            <div className="bg-gray-50 border border-gray-100 rounded p-4 text-center">
              <div className="text-2xl font-bold text-gray-900">{prMetrics.summary?.totalPrsMerged || 0}</div>
              <div className="text-sm text-gray-500">PRs Merged</div>
            </div>
            <div className="bg-gray-50 border border-gray-100 rounded p-4 text-center">
              <div className="text-2xl font-bold text-gray-900">{prMetrics.summary?.totalSecurityFixes || 0}</div>
              <div className="text-sm text-gray-500">Security Fixes</div>
            </div>
            <div className="bg-gray-50 border border-gray-100 rounded p-4 text-center">
              <div className="text-2xl font-bold text-gray-900">{prMetrics.summary?.mergeRate || '0%'}</div>
              <div className="text-sm text-gray-500">Merge Rate</div>
            </div>
          </div>

          {/* PR Metrics Chart */}
          <div className="h-80 w-full">
            {prMetrics.timeSeriesData && prMetrics.timeSeriesData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={prMetrics.timeSeriesData}
                  margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis
                    dataKey="date"
                    stroke="#6b7280"
                    tickFormatter={(date) => new Date(date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                  />
                  <YAxis stroke="#6b7280" />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#ffffff', border: '1px solid #e5e7eb', borderRadius: '0.5rem' }}
                    labelStyle={{ color: '#111827' }}
                    formatter={(value) => [value, '']}
                    labelFormatter={(date) => new Date(date).toLocaleDateString()}
                  />
                  <Legend />
                  <Line type="monotone" dataKey="prsCreated" name="PRs Created" stroke="#0d9488" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="prsMerged" name="PRs Merged" stroke="#10b981" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="securityFixes" name="Security Fixes" stroke="#f97316" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center">
                <p className="text-gray-500">No data available for the selected time period</p>
              </div>
            )}
          </div>
        </div>

        {/* Recently Merged Pull Requests */}
        <div className="bg-white border border-gray-100 shadow-sm rounded-lg p-6 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Recently Merged Pull Requests</h3>
          {mergedPullRequests.length > 0 ? (
            <ul className="space-y-2">
              {mergedPullRequests.map((pr) => (
                <li key={pr.number} className="flex items-center gap-2">
                  <a
                    href={pr.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-teal-600 hover:underline font-medium"
                  >
                    {pr.title || `PR #${pr.number}`}
                  </a>
                  <span className="text-xs text-gray-500">({pr.repo_url})</span>
                  <span className="text-xs text-gray-500 ml-2">
                    {pr.merged_at ? new Date(pr.merged_at).toLocaleString() : ''}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-gray-500">No recently merged pull requests.</div>
          )}
        </div>

        {/* Mean Time to Remediate */}
         {/* <div className="bg-white border border-gray-100 shadow-sm rounded-lg p-6 mb-6"> */}
          {/* <h3 className="text-lg font-semibold text-gray-900 mb-4">Mean Time to Remediate</h3>
          <div className="flex items-baseline gap-2">
            <span className="text-4xl font-bold text-gray-900">{dashboardStats.meanTimeToRemediate?.days || 0} days</span>
            <span className="text-green-600 text-sm">
              ↓ {dashboardStats.meanTimeToRemediate?.improvement || '0%'} improvement
            </span>
          </div> */}
        {/* </div>  */}

        {/* Latest Security Findings */}
        <div className="bg-white border border-gray-100 shadow-sm rounded-lg p-6 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Latest Security Findings</h3>
          {latestFindings.length > 0 ? (
            <div className="space-y-4">
              {latestFindings.map((finding, index) => (
                <div key={index} className="bg-gray-50 border border-gray-100 rounded p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`text-${finding.severity === 'CRITICAL' ? 'red' : finding.severity === 'HIGH' ? 'orange' : 'blue'}-600 text-sm font-mono`}>
                      [{finding.severity}]
                    </span>
                    <span className="text-gray-900 font-semibold">{finding.title}</span>
                  </div>
                  <p className="text-gray-600 text-sm">{finding.description}</p>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4 opacity-50" />
              <p className="text-gray-500">No recent findings to display</p>
            </div>
          )}
        </div>

        {/* Add LogoutButton */}
        <div className="flex justify-end mt-8">
          <LogoutButton />
        </div>

      </div>
    </div>
  );
};

export default DashboardPage;
